import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHeaderButton } from "@/contexts/header-button-context";
import {
  useGetPermissions,
  useGetRole,
  useGetRolePermissions,
  useUpdateRole,
  useUpdateRolePermissions,
} from "@/queries/roles";
import { useNavigate, useParams } from "react-router";
import { schema, type NewRoleFormData } from "@/schemas/roles/new-roles";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { AxiosError } from "axios";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import {
  useAssignUserToRole,
  useGetUsers,
  useUnassignUserToRole,
} from "@/queries/users/users.api";
import type { UserType } from "@/types/entities/users";
import {
  buildPermissionsMap,
  computeRoleType,
  filterPermissionsByQuery,
} from "./use-role-edit.helpers";

export const useRoleEdit = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { hasPermission } = useAuthorization();
  const canAssingRoleToUsers = hasPermission(Permissions.users["assign-role"]);
  const canAssignPermissions = hasPermission(
    Permissions.roles["assign-permissions"],
  );

  const {
    mutateAsync: mutateRolePermissions,
    isPending: isLoadingPermissionUpdate,
  } = useUpdateRolePermissions(params.id ?? "");

  const { mutateAsync: mutateRoleInfo, isPending: isLoadingRoleUpdate } =
    useUpdateRole(params.id ?? "");

  const { data: role } = useGetRole(params.id ?? "");
  const { data: permissions, isLoading: isLoadingPermissions } =
    useGetPermissions();
  const { data: rolePermissions, isLoading: isLoadingRolePermissions } =
    useGetRolePermissions(params.id ?? "");

  const { mutateAsync: assignUsersAsync, isPending: isLoadingAssignUsers } =
    useAssignUserToRole(params.id ?? "");
  const { mutateAsync: unassignUsersAsync, isPending: isLoadingUnassignUsers } =
    useUnassignUserToRole(params.id ?? "");

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<NewRoleFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: role?.data?.name || "",
      description: role?.data?.description || "",
    },
  });

  const [name, description] = watch(["name", "description"]);

  const permissionsMap = useMemo(
    () => buildPermissionsMap(permissions),
    [permissions],
  );

  const [localRolePermissions, setLocalRolePermissions] = useState<
    {
      name: string;
      id: string;
    }[]
  >(
    rolePermissions?.map((perm) => {
      return {
        name: perm.name,
        id: perm.id,
      };
    }) || [],
  );

  useEffect(() => {
    setLocalRolePermissions(
      rolePermissions?.map((perm) => {
        return {
          name: perm.name,
          id: perm.id,
        };
      }) || [],
    );
  }, [rolePermissions]);

  const handleCheckboxChange = (id: string) => {
    setLocalRolePermissions((prev) => {
      const exists = prev.some((permission) => permission.id === id);
      if (exists) {
        return prev.filter((permission) => permission.id !== id);
      } else {
        const permission = permissions?.find((p) => p.id === id);
        if (permission) {
          return [...prev, { name: permission.name, id: permission.id }];
        }
        return prev;
      }
    });
  };

  const hasPermissionsLoaded = isLoadingRolePermissions || isLoadingPermissions;
  const hasPermissionsChaged = useMemo(() => {
    return (
      localRolePermissions.length !== rolePermissions?.length ||
      localRolePermissions.some(
        (perm) => !rolePermissions?.some((p) => p.id === perm.id),
      )
    );
  }, [localRolePermissions, rolePermissions]);

  const [activeTab, setActiveTab] = useState<"permissions" | "users">(() => {
    if (canAssignPermissions) {
      return "permissions";
    }
    return "users";
  });

  const { data: users, isLoading: isLoadingUsers } = useGetUsers({
    page: 1,
    per_page: 100000,
    role: params.id!,
    relationships: ["userRoles", "createdBy"],
  });

  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<
    UserType[]
  >([]);
  const [selectedUsersToUnassign, setSelectedUsersToUnassign] = useState<
    string[]
  >([]);

  const handleUserAssigment = (user: UserType) => {
    const userId = user.keycloak_id || "";
    const wasMarkedForUnassignment = selectedUsersToUnassign.includes(userId);
    if (wasMarkedForUnassignment) {
      setSelectedUsersToUnassign((prev) => prev.filter((id) => id !== userId));
    } else {
      setSelectedUsersToAssign((prev) => {
        if (prev.some((u) => u.keycloak_id === userId)) return prev;
        return [...prev, user];
      });
    }
  };

  const onUnassign = (userId: string) => {
    const wasMarkedForAssignment = selectedUsersToAssign.some(
      (user) => user.keycloak_id === userId,
    );
    if (wasMarkedForAssignment) {
      setSelectedUsersToAssign((prev) =>
        prev.filter((user) => user.keycloak_id !== userId),
      );
    } else {
      setSelectedUsersToUnassign((prev) => {
        if (prev.includes(userId)) return prev;
        return [...prev, userId];
      });
    }
  };

  const [localSearchQuery, setLocalSearchQuery] = useState<string>("");
  const filterPermissions = useMemo(
    () => filterPermissionsByQuery(permissionsMap, localSearchQuery),
    [permissionsMap, localSearchQuery],
  );

  const roleTypeByPermissions = useMemo(
    () => computeRoleType(localRolePermissions),
    [localRolePermissions],
  );

  const [tempData, setTempData] = useState<NewRoleFormData | null>(null);
  const onPreSubmit = (data: NewRoleFormData) => {
    setModalState("edit");
    setTempData(data);
  };

  const asignUsersToRole = useCallback(async () => {
    if (!canAssingRoleToUsers || selectedUsersToAssign.length === 0) return;
    try {
      await assignUsersAsync(
        selectedUsersToAssign
          .map((user) => user.keycloak_id || "")
          .filter(Boolean),
      );
    } catch (e) {
      throw e instanceof Error ? e : new Error(String(e));
    }
  }, [assignUsersAsync, canAssingRoleToUsers, selectedUsersToAssign]);

  const unassignUsersFromRole = useCallback(async () => {
    if (!canAssingRoleToUsers || selectedUsersToUnassign.length === 0) return;
    try {
      await unassignUsersAsync(
        selectedUsersToUnassign.filter(Boolean).filter((userId) => {
          return selectedUsersToAssign.every(
            (user) => user.keycloak_id !== userId,
          );
        }),
      );
    } catch (e) {
      throw e instanceof Error ? e : new Error(String(e));
    }
  }, [
    unassignUsersAsync,
    canAssingRoleToUsers,
    selectedUsersToUnassign,
    selectedUsersToAssign,
  ]);

  const onSubmit = async (data: NewRoleFormData) => {
    try {
      if (role?.data?.is_editable) {
        await mutateRoleInfo({
          ...data,
          id: params.id!,
          type: roleTypeByPermissions,
        });
      }
      if (hasPermissionsChaged && canAssignPermissions) {
        await mutateRolePermissions(
          localRolePermissions.map((perm) => perm.id),
        );
      }

      await asignUsersToRole();
      await unassignUsersFromRole();
      showToast({
        title: "Rol actualizado",
        description: "El rol ha sido actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
      navigate(`/roles`);
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      if (
        e.status === 409 &&
        e.response?.data.message.includes("already exists")
      ) {
        setError("name", {
          type: "manual",
          message: "El nombre del rol ya existe. Por favor, elige otro.",
        });
      }
      setSelectedUsersToAssign([]);
      setSelectedUsersToUnassign([]);
      showToast({
        title: "Error al actualizar el rol",
        description: "Ha ocurrido un error al actualizar el rol.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const [modalState, setModalState] = useState<"edit" | "idle">("idle");

  const { setComp } = useHeaderButton();

  useEffect(() => {
    if (role?.data?.is_editable) {
      setComp(
        <>
          <Button
            variant="quiet"
            size="xs"
            type="button"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="xs"
            disabled={
              isLoadingPermissionUpdate ||
              hasPermissionsLoaded ||
              isLoadingRoleUpdate ||
              localRolePermissions.length === 0 ||
              (!hasPermissionsChaged &&
                (!isDirty || !isValid) &&
                canAssingRoleToUsers &&
                selectedUsersToAssign.length === 0 &&
                selectedUsersToUnassign.length === 0) ||
              isLoadingAssignUsers ||
              isLoadingUnassignUsers
            }
            form="edit-role-form"
            type="submit"
          >
            Guardar cambios
            {(isLoadingPermissionUpdate ||
              isLoadingRoleUpdate ||
              isLoadingAssignUsers ||
              isLoadingUnassignUsers) && (
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin text-current"
              />
            )}
          </Button>
        </>,
      );
    } else {
      setComp(
        <>
          <Button
            variant="quiet"
            size="xs"
            type="button"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="xs"
            type="button"
            disabled={
              (canAssingRoleToUsers &&
                selectedUsersToAssign.length === 0 &&
                selectedUsersToUnassign.length === 0) ||
              isLoadingUnassignUsers ||
              isLoadingAssignUsers
            }
            onClick={async () => {
              try {
                if (canAssingRoleToUsers && selectedUsersToAssign.length > 0) {
                  await asignUsersToRole();
                }
                if (
                  canAssingRoleToUsers &&
                  selectedUsersToUnassign.length > 0
                ) {
                  await unassignUsersFromRole();
                }
                showToast({
                  title: "Rol actualizado",
                  description:
                    "Los usuarios han sido actualizados correctamente.",
                  options: {
                    type: "success",
                    duration: 5000,
                  },
                });
                navigate(`/roles`);
              } catch (e) {
                console.log(e);
                showToast({
                  title: "Error al actualizar el rol",
                  description:
                    "Ha ocurrido un error al actualizar los usuarios del rol.",
                  options: {
                    type: "error",
                    duration: 5000,
                  },
                });
              }
            }}
          >
            Guardar cambios
            {(isLoadingPermissionUpdate ||
              isLoadingAssignUsers ||
              isLoadingUnassignUsers) && (
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin text-current"
              />
            )}
          </Button>
        </>,
      );
    }

    return () => {
      setComp(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    role,
    isDirty,
    navigate,
    setComp,
    isValid,
    unassignUsersFromRole,
    asignUsersToRole,
    selectedUsersToUnassign,
    isLoadingUnassignUsers,
    isLoadingAssignUsers,
    canAssingRoleToUsers,
    selectedUsersToAssign,
    isLoadingRoleUpdate,
    hasPermissionsChaged,
    isLoadingPermissionUpdate,
    hasPermissionsLoaded,
    localRolePermissions,
  ]);

  return {
    role,
    modalState,
    setModalState,
    tempData,
    onSubmit,
    handleSubmit,
    onPreSubmit,
    register,
    errors,
    name,
    description,
    roleTypeByPermissions,
    canAssignPermissions,
    canAssingRoleToUsers,
    activeTab,
    setActiveTab,
    setLocalSearchQuery,
    handleUserAssigment,
    selectedUsersToAssign,
    isLoadingPermissions,
    isLoadingRolePermissions,
    filterPermissions,
    handleCheckboxChange,
    localRolePermissions,
    users,
    isLoadingUsers,
    selectedUsersToUnassign,
    onUnassign,
  };
};
